import React from 'react';

/**
 * Top-level error boundary. A single render/lifecycle throw anywhere in the
 * tree would otherwise unmount the whole app and leave a blank white screen.
 * This catches those errors and shows a friendly, recoverable fallback so the
 * user can reload or head home instead of staring at nothing.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep a console trail for debugging; a real error-reporting hook (Sentry,
    // etc.) can be wired in here later.
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error caught by ErrorBoundary:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#0b0b12',
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          color: '#f3f1ea',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 460,
            textAlign: 'center',
            background: 'rgba(255,255,255,0.035)',
            border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 18,
            padding: '40px 32px',
            boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
          }}
        >
          <div style={{ fontSize: 44, marginBottom: 16 }}>
            <i className="bi bi-exclamation-triangle" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(243,241,234,0.65)', margin: '0 0 24px' }}>
            An unexpected error stopped this page from loading. Reloading usually
            fixes it. If the problem keeps happening, please contact support.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                padding: '12px 20px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #C9A84C, #b08f3c)',
                color: '#1a1505',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
            <a
              href="/"
              style={{
                padding: '12px 20px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'transparent',
                color: '#f3f1ea',
                fontWeight: 600,
                fontSize: 14,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
